import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormControlLabel as MuiFormControlLabel,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { SingleImageDisplay } from '../../components/ImageDisplay';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface User {
  _id: string;
  email: string;
  parent: {
    _id: string;
    fullName: string;
    photo: string;
  } | null;
  kindergarten: {
    _id: string;
    name: string;
  };
  accessLevel: 'basic' | 'full';
  permissions: {
    viewChildren: boolean;
    viewMenu: boolean;
    viewCameras: boolean;
    receiveNotifications: boolean;
  };
  isActive: boolean;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
}

interface PasswordChangeResult {
  email: string;
  newPassword: string;
  parentName: string;
}

const Users: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [viewingUser, setViewingUser] = useState<User | null>(null);
  const [passwordMethod, setPasswordMethod] = useState<'generate' | 'manual'>('generate');
  const [manualPassword, setManualPassword] = useState('');
  const [passwordResult, setPasswordResult] = useState<PasswordChangeResult | null>(null);
  const [showPasswordResult, setShowPasswordResult] = useState(false);

  // Налаштування axios
  const api = axios.create({
    baseURL: 'https://api.smartik.com.ua/api'
  });

  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  useEffect(() => {
    fetchUsers();
    const onFocus = () => fetchUsers();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users?role=parent');
      setUsers(response.data.data || []);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/users/${userId}/status`, { isActive });
      fetchUsers(); // Оновлюємо список
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка зміни статусу');
    }
  };

  const handleOpenPasswordDialog = (user: User) => {
    setSelectedUser(user);
    setPasswordMethod('generate');
    setManualPassword('');
    setOpenPasswordDialog(true);
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setSelectedUser(null);
    setPasswordMethod('generate');
    setManualPassword('');
  };

  const handleOpenViewDialog = (user: User) => {
    setViewingUser(user);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewingUser(null);
  };

  const handleChangePassword = async () => {
    if (!selectedUser) return;

    try {
      const requestData = passwordMethod === 'generate' 
        ? { generateNew: true }
        : { password: manualPassword };

      const response = await api.patch(`/users/${selectedUser._id}/password`, requestData);
      
      if (response.data.success) {
        setPasswordResult({
          email: response.data.data.user.email,
          newPassword: response.data.data.newPassword,
          parentName: response.data.data.user.parentName,
        });
        setShowPasswordResult(true);
        handleClosePasswordDialog();
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Помилка зміни пароля');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'full':
        return 'success';
      case 'basic':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Користувачі
        </Typography>
        <Button variant="outlined" onClick={() => fetchUsers()}>
          Оновити
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Користувач</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Дитячий садок</TableCell>
                  <TableCell>Рівень доступу</TableCell>
                  <TableCell>Дозволи</TableCell>
                  <TableCell>Останній вхід</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Дії</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SingleImageDisplay
                          image={user.parent?.photo || ''}
                          size={40}
                          title="Фото користувача"
                          placeholderIcon={<PersonIcon />}
                          placeholderText="Немає фото"
                        />
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {user.parent?.fullName || 'Невідомий батько'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {user._id.slice(-6)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {user.kindergarten?.name || 'Не вказано'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.accessLevel === 'full' ? 'Повний' : 'Базовий'}
                        color={getAccessLevelColor(user.accessLevel)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.permissions.viewChildren && (
                          <Chip label="Діти" size="small" variant="outlined" />
                        )}
                        {user.permissions.viewMenu && (
                          <Chip label="Меню" size="small" variant="outlined" />
                        )}
                        {user.permissions.viewCameras && (
                          <Chip label="Камери" size="small" variant="outlined" />
                        )}
                        {user.permissions.receiveNotifications && (
                          <Chip label="Сповіщення" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLogin ? formatDate(user.lastLogin) : 'Ніколи'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={user.isActive}
                            onChange={(e) => handleToggleStatus(user._id, e.target.checked)}
                            color="success"
                          />
                        }
                        label=""
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Переглянути">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenViewDialog(user)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('admins', 'update') && (
                          <Tooltip title="Змінити пароль">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleOpenPasswordDialog(user)}
                            >
                              <LockIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('admins', 'update') && (
                          <Tooltip title={user.isActive ? 'Блокувати' : 'Розблокувати'}>
                            <IconButton 
                              size="small" 
                              color={user.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleStatus(user._id, !user.isActive)}
                            >
                              {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог зміни пароля */}
      <Dialog 
        open={openPasswordDialog} 
        onClose={handleClosePasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Змінити пароль
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Користувач: <strong>{selectedUser?.parent?.fullName || 'Невідомий батько'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Email: {selectedUser?.email}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Виберіть спосіб зміни пароля:
              </Typography>
              <RadioGroup
                value={passwordMethod}
                onChange={(e) => setPasswordMethod(e.target.value as 'generate' | 'manual')}
              >
                <MuiFormControlLabel
                  value="generate"
                  control={<Radio />}
                  label="Згенерувати новий пароль автоматично"
                />
                <MuiFormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Ввести пароль вручну"
                />
              </RadioGroup>
            </FormControl>

            {passwordMethod === 'manual' && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  label="Новий пароль"
                  type="password"
                  value={manualPassword}
                  onChange={(e) => setManualPassword(e.target.value)}
                  helperText="Мінімум 6 символів"
                  error={manualPassword.length > 0 && manualPassword.length < 6}
                />
              </Box>
            )}

            {passwordMethod === 'generate' && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Пароль буде згенерований автоматично з 8 символів (літери та цифри)
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>Скасувати</Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained"
            disabled={passwordMethod === 'manual' && (manualPassword.length < 6)}
          >
            Змінити пароль
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог перегляду користувача */}
      <Dialog 
        open={openViewDialog} 
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Інформація про користувача
        </DialogTitle>
        <DialogContent>
          {viewingUser && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {viewingUser.parent?.fullName || 'Невідомий батько'}
                  </Typography>
                  <Chip 
                    label={viewingUser.isActive ? 'Активний' : 'Неактивний'} 
                    color={viewingUser.isActive ? 'success' : 'default'} 
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    {viewingUser.email}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Рівень доступу
                  </Typography>
                  <Chip 
                    label={viewingUser.accessLevel === 'full' ? 'Повний' : 'Базовий'} 
                    color={getAccessLevelColor(viewingUser.accessLevel) as any} 
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Дитячий садок
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon fontSize="small" color="action" />
                    {viewingUser.kindergarten ? viewingUser.kindergarten.name : 'Не вказано'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Останній вхід
                  </Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    {viewingUser.lastLogin ? formatDate(viewingUser.lastLogin) : 'Ніколи'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Дозволи
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {viewingUser.permissions.viewChildren && (
                    <Chip label="Перегляд дітей" color="primary" size="small" />
                  )}
                  {viewingUser.permissions.viewMenu && (
                    <Chip label="Перегляд меню" color="primary" size="small" />
                  )}
                  {viewingUser.permissions.viewCameras && (
                    <Chip label="Перегляд камер" color="primary" size="small" />
                  )}
                  {viewingUser.permissions.receiveNotifications && (
                    <Chip label="Отримання сповіщень" color="primary" size="small" />
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Додаткова інформація
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Дата створення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingUser.createdAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Останнє оновлення
                    </Typography>
                    <Typography variant="body2">
                      {new Date(viewingUser.updatedAt).toLocaleDateString('uk-UA')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Діалог з результатом зміни пароля */}
      <Dialog 
        open={showPasswordResult} 
        onClose={() => setShowPasswordResult(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Пароль змінено
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Для користувача <strong>{passwordResult?.parentName}</strong> змінено пароль:
            </Typography>
            
            <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Email:</strong> {passwordResult?.email}
              </Typography>
              <Typography variant="body2">
                <strong>Новий пароль:</strong> {passwordResult?.newPassword}
              </Typography>
            </Box>
            
            <Alert severity="warning">
              Збережіть ці дані! Пароль не можна буде подивитися пізніше.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordResult(false)} variant="contained">
            Зрозуміло
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
